import { Container } from "react-grid-system";
import AuthorMetaData from "@components/blog/AuthorMetaData";
import BlogPost from "@components/blog/BlogPost";
import CardLayout from "@components/layout/CardLayout";
import Hero from "@components/layout/Hero";
import Tags from "@components/Tags";
import { formatPublishDate, stringToURI } from "@scripts/utils";

type BluesUniversityPageProps = {
  posts: BlogPost[];
};

const renderCards = (posts: BlogPost[]) => (
  <CardLayout
    cards={posts.map((post) => ({
      link: `/blog/${post.slug}`,
      title: post.title,
      description: post.excerpt,
      banner: post.banner,
      date: formatPublishDate(post.publishedAt),
      descriptionExtra: (
        <Tags
          tags={post.tags.map((tag) => ({
            name: tag,
            url: `/blog/tag/${stringToURI(tag)}`,
          }))}
        />
      ),
      footerExtra: <AuthorMetaData author={post.author} />,
    }))}
  />
);

const sortPosts = (posts: BlogPost[]) =>
  posts.sort((a, b) => a.publishedAt.localeCompare(b.publishedAt));

const BluesUniversityPage = ({ posts }: BluesUniversityPageProps) => {
  const hardwarePosts = sortPosts(
    posts.filter((post) => post.tags.includes("Hardware")),
  );
  const firmwarePosts = sortPosts(
    posts.filter((post) => post.tags.includes("Firmware")),
  );
  const cloudPosts = sortPosts(
    posts.filter((post) => post.tags.includes("Cloud")),
  );
  const otherPosts = sortPosts(
    posts.filter(
      (post) =>
        !post.tags.includes("Hardware") &&
        !post.tags.includes("Firmware") &&
        !post.tags.includes("Cloud"),
    ),
  );

  return (
    <>
      <Hero
        title="Blues University"
        subtitle="An ongoing series of articles geared towards beginner- and intermediate-level developers who are looking to expand their knowledge of embedded development and the IoT"
        size="compact"
      />
      <Container>
        <div className="blues-university-page">
          <h2>First Steps</h2>
          {renderCards(otherPosts)}

          {hardwarePosts.length > 0 && (
            <>
              <h2 className="dotted-divider">Hardware</h2>
              {renderCards(hardwarePosts)}
            </>
          )}

          {firmwarePosts.length > 0 && (
            <>
              <h2 className="dotted-divider">Firmware</h2>
              {renderCards(firmwarePosts)}
            </>
          )}

          {cloudPosts.length > 0 && (
            <>
              <h2 className="dotted-divider">Cloud</h2>
              {renderCards(cloudPosts)}
            </>
          )}
        </div>
      </Container>
      <style jsx>{`
        .blues-university-page {
          padding: 2rem 1rem;
        }
      `}</style>
    </>
  );
};

export default BluesUniversityPage;
